import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, ResponsiveContainer } from 'recharts';
import { Wifi, WifiOff, RefreshCw, Activity, Clock, Edit3, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { EndpointEditModal } from '@/components/EndpointEditModal';
import { useToast } from '@/components/ui/Toast';

interface MonitoredEndpoint {
  id: number; name: string; description: string | null; kind: string;
  ip_address: string; mac_address: string | null;
  probe_method: string; tcp_port: number | null; poll_interval_sec: number;
  enabled: boolean; silent: boolean; auto_discovered: boolean;
  last_probe_at: string | null; last_status: string; last_latency_ms: number | null;
  customer?: { id: number; name: string; external_id: string | null } | null;
}

interface ProbeBucket { bucket: string; ok: boolean; latency_ms: number | null; failures: number; samples: number; }
interface Transition { id: number; at: string; from_state: string; to_state: string; duration_sec: number | null; }

export default function EndpointDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [hours, setHours] = useState(24);
  const [editOpen, setEditOpen] = useState(false);
  const qc = useQueryClient();

  const { data: ep } = useQuery({
    queryKey: ['endpoint', id],
    queryFn: () => api.get<{ data: MonitoredEndpoint }>(`/endpoints/${id}`).then((r) => r.data.data),
    refetchInterval: 30000,
    enabled: !!id,
  });

  const { data: history } = useQuery({
    queryKey: ['endpoint-history', id, hours],
    queryFn: () => api.get<{ data: { probes: ProbeBucket[]; transitions: Transition[] }; meta: { since: string; bucket: string; hours: number } }>(
      `/endpoints/${id}/history`, { params: { hours } }
    ).then((r) => r.data),
    refetchInterval: 60000,
    enabled: !!id,
  });

  const { data: recentProbes } = useQuery({
    queryKey: ['endpoint-recent-probes', id],
    queryFn: () => api.get<{ data: Array<{ at: string; ok: boolean; latency_ms: number | null; method_used: string | null }> }>(
      `/endpoints/${id}/recent-probes`, { params: { limit: 30 } }
    ).then((r) => r.data.data),
    refetchInterval: 30000,
    enabled: !!id && ep?.probe_method === 'dns_query',
  });

  const probeMut = useMutation({
    mutationFn: () => api.post(`/endpoints/${id}/probe`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['endpoint', id] }),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/endpoints/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.push({ kind: 'success', title: 'Endpoint supprimé' });
      qc.invalidateQueries({ queryKey: ['endpoints'] });
      navigate('/app/endpoints');
    },
    onError: (e: any) => toast.push({ kind: 'error', title: 'Erreur · ' + (e?.response?.data?.message ?? e.message ?? 'inconnue') }),
  });

  if (!ep) return <div className="p-8 text-text-muted text-sm">Chargement…</div>;

  // Build chart data
  const chartData = (history?.data.probes ?? []).map((b) => ({
    t: new Date(b.bucket).getTime(),
    label: new Date(b.bucket).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    latency: b.ok ? (b.latency_ms ?? 0) : null,
    ok: b.ok,
  }));

  // Trouve les périodes down pour les ReferenceArea
  const downBands: Array<{ x1: number; x2: number }> = [];
  for (let i = 0; i < chartData.length; i++) {
    if (chartData[i].ok === false) {
      const start = chartData[i].t;
      let j = i;
      while (j + 1 < chartData.length && chartData[j + 1].ok === false) j++;
      downBands.push({ x1: start, x2: chartData[j].t });
      i = j;
    }
  }

  const statusColor = ep.last_status === 'reachable' ? 'text-success' : ep.last_status === 'unreachable' ? 'text-danger' : 'text-text-faint';
  const transitions = (history?.data.transitions ?? []).slice().reverse();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title={ep.name}
        subtitle={<span className={statusColor}>{ep.last_status}{ep.last_latency_ms !== null && ` · ${ep.last_latency_ms}ms`}</span>}
        actions={
          <>
            <Link to="/app/endpoints" className="btn btn-sm">← Liste</Link>
            <button className="btn btn-sm" onClick={() => setEditOpen(true)}>
              <Edit3 size={11} />Modifier
            </button>
            <button className="btn btn-sm text-danger" onClick={() => {
              if (window.confirm(`Supprimer "${ep.name}" ? Les probes en cours s'arrêteront. L'historique reste dans la DB.`)) deleteMut.mutate();
            }} disabled={deleteMut.isPending}>
              <Trash2 size={11} />Supprimer
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => probeMut.mutate()} disabled={probeMut.isPending}>
              <RefreshCw size={11} className={probeMut.isPending ? 'animate-spin' : ''} />Probe now
            </button>
          </>
        }
      />

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Métadonnées */}
        <div className="card p-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <Field label="Type" value={ep.kind.replace(/_/g, ' ')} mono />
          <Field label="IP" value={ep.ip_address} mono />
          <Field label="MAC" value={ep.mac_address ?? '—'} mono />
          <Field label="Méthode" value={`${ep.probe_method}${ep.tcp_port ? ` :${ep.tcp_port}` : ''}`} mono />
          <Field label="Poll" value={`${ep.poll_interval_sec}s`} mono />
          <Field label="Customer" value={ep.customer ? <Link to={`/app/customers/${ep.customer.id}`} className="hover:text-brand">{ep.customer.name}</Link> : '—'} />
          <Field label="Activé" value={ep.enabled ? 'oui' : 'non'} />
          <Field label="Silent" value={ep.silent ? 'oui (pas d\'alerte)' : 'non'} />
          {ep.description && <div className="col-span-full"><Field label="Description" value={ep.description} /></div>}
        </div>

        {/* Graph latence */}
        <div className="card">
          <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Activity size={13} />Latence · bucket {history?.meta.bucket ?? '—'}</h3>
            <div className="flex items-center gap-1 bg-bg-2 border border-border rounded text-xs">
              {[{ h: 24, l: '1j' }, { h: 24 * 7, l: '7j' }, { h: 24 * 30, l: '30j' }].map((opt) => (
                <button key={opt.h} onClick={() => setHours(opt.h)}
                  className={`h-6 px-2 ${hours === opt.h ? 'bg-surface-hi text-text' : 'text-text-muted hover:text-text'}`}>
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div className="p-3">
            {chartData.length === 0 ? (
              <div className="text-center py-8 text-text-faint italic text-xs">Aucune donnée sur cette période. Attendez le prochain cycle de probe…</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="grad-lat" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#56b4e9" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#56b4e9" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#2e3038" strokeDasharray="2 2" />
                  <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} scale="time"
                    tickFormatter={(v) => new Date(v).toLocaleTimeString('fr', { hour: '2-digit', minute: '2-digit' })}
                    stroke="#6c7079" fontSize={10} />
                  <YAxis stroke="#6c7079" fontSize={10} unit="ms" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: '#21232a', border: '1px solid #3a3c46', fontSize: 11 }}
                    labelFormatter={(v) => new Date(v).toLocaleString('fr')}
                    formatter={(value: unknown) => (value === null || value === undefined) ? ['down', 'État'] as [string, string] : [`${value} ms`, 'Latence'] as [string, string]}
                  />
                  {downBands.map((b, i) => (
                    <ReferenceArea key={i} x1={b.x1} x2={b.x2} y1={0} y2="auto" fill="#ef4444" fillOpacity={0.18} ifOverflow="extendDomain" />
                  ))}
                  <Area type="monotone" dataKey="latency" stroke="#56b4e9" strokeWidth={1.5} fill="url(#grad-lat)" connectNulls={false} name="Latence" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Derniers tests DNS (rotation parmi 80+ domaines) — affiché seulement pour kind=dns_query */}
        {ep.probe_method === 'dns_query' && recentProbes && recentProbes.length > 0 && (
          <div className="card">
            <div className="px-3 py-2.5 border-b border-border">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Activity size={13} />Derniers tests DNS ({recentProbes.length} récents)
              </h3>
              <p className="text-[10.5px] text-text-faint mt-0.5">
                Rotation parmi ~82 domaines (QC banques/gov/médias + global). 1 domaine testé par cycle.
              </p>
            </div>
            <div className="max-h-80 overflow-auto">
              <table className="luc-table text-[11px]">
                <thead className="sticky top-0 bg-bg z-10">
                  <tr>
                    <th>Heure</th>
                    <th>Domaine testé</th>
                    <th>Résultat</th>
                    <th>Latence</th>
                  </tr>
                </thead>
                <tbody>
                  {recentProbes.map((p, i) => {
                    const domain = p.method_used?.startsWith('dns_query:') ? p.method_used.slice(10) : '—';
                    return (
                      <tr key={i}>
                        <td className="font-mono text-text-faint whitespace-nowrap">
                          {new Date(p.at).toLocaleString('fr', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </td>
                        <td className="font-mono text-text-muted">{domain}</td>
                        <td>
                          {p.ok
                            ? <span className="text-success">✓ resolved</span>
                            : <span className="text-danger">✗ failed</span>}
                        </td>
                        <td className="font-mono">
                          {p.latency_ms !== null ? <span className={p.latency_ms < 50 ? 'text-success' : p.latency_ms < 200 ? 'text-warning' : 'text-danger'}>{p.latency_ms}ms</span> : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transitions (conservées forever) */}
        <div className="card">
          <div className="px-3 py-2.5 border-b border-border">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Clock size={13} />Transitions d'état ({transitions.length})</h3>
          </div>
          {transitions.length === 0 ? (
            <div className="p-6 text-center text-text-faint italic text-xs">Aucune transition enregistrée.</div>
          ) : (
            <table className="luc-table text-xs">
              <thead><tr><th>Heure</th><th>De</th><th></th><th>À</th><th>Durée état précédent</th></tr></thead>
              <tbody>
                {transitions.map((t) => (
                  <tr key={t.id}>
                    <td className="font-mono text-text-faint">{new Date(t.at).toLocaleString('fr')}</td>
                    <td><StateBadge state={t.from_state} /></td>
                    <td className="text-text-faint">→</td>
                    <td><StateBadge state={t.to_state} /></td>
                    <td className="text-text-muted">{t.duration_sec ? fmtDur(t.duration_sec) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <EndpointEditModal open={editOpen} onClose={() => setEditOpen(false)} editing={ep as any} />
    </div>
  );
}

function StateBadge({ state }: { state: string }) {
  if (state === 'reachable') return <span className="flex items-center gap-1 text-success text-[11px]"><Wifi size={10} />reachable</span>;
  if (state === 'unreachable') return <span className="flex items-center gap-1 text-danger text-[11px]"><WifiOff size={10} />unreachable</span>;
  return <span className="text-text-faint text-[11px]">{state}</span>;
}

function fmtDur(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}min`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}j`;
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-text-faint font-medium">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} text-xs text-text`}>{value}</span>
    </div>
  );
}
